import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { createTemplateOutput, createTemplatePayloadUpdate } from '@/tools/models/templateStore.utilities';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type {
  DerivedFeatureWithColumnDefinition,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateOutput,
  TemplatePayload,
} from '@/tools/models/template.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

/** Enforces that ScalingTemplateState always has the same shape as ToolTemplateState. */

export interface ScalingTemplateState extends BaseToolPersistedState {
  /** Stable container GUID — the canonical identity of the template across versions. */
  id: string;
  /** GUID of the specific template version currently loaded (draft or initial unlocked template). */
  versionId: string;
  /** Human-readable description of the template. */
  description: string;
  /** Variable inputs the user maps before applying the template. Includes the instance-name synthetic input. */
  inputFeatures: TemplateInput[];
  /**
   * Output features computed by the template. The union reflects different representations used across
   * the tool (apply), edit, and scaling-table contexts; narrow to the expected type at the call site.
   */
  derivedFeatures: TemplateOutput[] | ColumnDefinition[] | DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[];
  /** Display name of the template. */
  name: string;
  /** Workbook ID this template is scoped to, or undefined for shared/global templates. */
  scopedTo: string | undefined;
  /** Datasource ID identifying which template library this template belongs to. */
  datasourceId: string | undefined;
  /** ID of the feature currently open in the feature-edit panel; undefined when no panel is open. */
  featureToEditId: string | undefined;
  /** ID of the instance being re-applied or edited; undefined on the new-application flow. */
  instanceToEditId: string | undefined;
  /** Derived features that already exist on the saved template version (used to diff against edits). */
  existingDerivedFeatures: TemplateDerivedFeature[];
  /** Input features from the saved template version, excluding the synthetic instance-name feature. */
  existingInputFeatures: TemplateInput[];
  /** Derived features added in the current editing session but not yet persisted. */
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  /** Input features added in the current editing session but not yet persisted. */
  newInputFeatures: NewlyAddedInputFeature[];
  /** The synthetic SEEQ_MODEL_INSTANCE_NAME input feature, kept separate so it is never lost during template updates. */
  instanceNameFeature: TemplateInput | undefined;
  /** Whether the loaded template version is locked (immutable). */
  isLocked: boolean;
  /** Whether the loaded template is a draft. */
  isDraft: boolean;
  /** Production version number this draft was created from. */
  sourceVersionNumber: number | undefined;
  /** Canonical feature IDs from the version this draft was created from. Undefined when not loaded. */
  editBaseFeatureCanonicalIds: string[] | undefined;
  /** Total number of instances for this template across all versions. */
  instanceCount: number | undefined;
}

export interface ScalingTemplateActions {
  reset: () => void;
  setTemplate: (template: TemplatePayload) => void;
  setFeatureToEditId: (featureToEditId: string | undefined) => void;
  setInstanceToEditId: (instanceToEditId: string | undefined) => void;
  setTemplateOutput: (templateOutput: TemplateOutputV1) => void;
  setVersionState: (params: {
    isLocked: boolean;
    isDraft: boolean;
    sourceVersionNumber?: number;
    editBaseFeatureCanonicalIds: string[] | undefined;
  }) => void;
  setInstanceCount: (instanceCount: number | undefined) => void;
}

export const initialScalingTemplateState: ScalingTemplateState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  id: '',
  versionId: '',
  description: '',
  inputFeatures: [],
  derivedFeatures: [],
  name: '',
  scopedTo: undefined,
  datasourceId: undefined,
  featureToEditId: undefined,
  instanceToEditId: undefined,
  existingDerivedFeatures: [],
  existingInputFeatures: [],
  newDerivedFeatures: [],
  newInputFeatures: [],
  instanceNameFeature: undefined,
  isLocked: false,
  isDraft: false,
  sourceVersionNumber: undefined,
  editBaseFeatureCanonicalIds: undefined,
  instanceCount: undefined,
});

export type ScalingTemplateDehydratedState = ScalingTemplateState;

const scalingTemplateActionsFactory: ActionsFactory<ScalingTemplateState, ScalingTemplateActions> = (
  set,
  _get,
  resetState,
) => ({
  reset: resetState,

  setTemplate: (template: TemplatePayload) =>
    set((draft: ScalingTemplateState) => {
      Object.assign(draft, createTemplatePayloadUpdate(template, draft.inputFeatures));
    }),

  setFeatureToEditId: (featureToEditId: string | undefined) =>
    set((draft: ScalingTemplateState) => {
      draft.featureToEditId = featureToEditId;
    }),

  setInstanceToEditId: (instanceToEditId: string | undefined) =>
    set((draft: ScalingTemplateState) => {
      draft.instanceToEditId = instanceToEditId;
    }),

  setTemplateOutput: (templateOutput: TemplateOutputV1) =>
    set((draft: ScalingTemplateState) => {
      Object.assign(draft, createTemplateOutput(templateOutput));
    }),

  setVersionState: ({ isLocked, isDraft, sourceVersionNumber, editBaseFeatureCanonicalIds }) =>
    set((draft: ScalingTemplateState) => {
      draft.isLocked = isLocked;
      draft.isDraft = isDraft;
      draft.sourceVersionNumber = sourceVersionNumber;
      draft.editBaseFeatureCanonicalIds = editBaseFeatureCanonicalIds;
    }),

  setInstanceCount: (instanceCount: number | undefined) =>
    set((draft: ScalingTemplateState) => {
      draft.instanceCount = instanceCount;
    }),
});

const [ScalingTemplateStoreProvider, useScalingTemplateStoreInstance, useScalingTemplateSelector] = createStore(
  'ScalingTemplate',
  initialScalingTemplateState,
  scalingTemplateActionsFactory,
);

export { ScalingTemplateStoreProvider };

export const dehydrateScalingTemplateState = (state: ScalingTemplateState): ScalingTemplateDehydratedState => state;

// Selector hooks
export const useScalingTemplateId = () => useScalingTemplateSelector((state) => state.id);
export const useScalingTemplateVersionId = () => useScalingTemplateSelector((state) => state.versionId);
export const useScalingTemplateName = () => useScalingTemplateSelector((state) => state.name);
export const useScalingTemplateDescription = () => useScalingTemplateSelector((state) => state.description);
export const useScalingTemplateScopedTo = () => useScalingTemplateSelector((state) => state.scopedTo);
export const useScalingTemplateDatasourceId = () => useScalingTemplateSelector((state) => state.datasourceId);
export const useScalingTemplateFeatureToEditId = () => useScalingTemplateSelector((state) => state.featureToEditId);
export const useScalingTemplateInstanceToEditId = () => useScalingTemplateSelector((state) => state.instanceToEditId);
export const useScalingTemplateIsLocked = () => useScalingTemplateSelector((state) => state.isLocked);
export const useScalingTemplateIsDraft = () => useScalingTemplateSelector((state) => state.isDraft);
export const useScalingTemplateSourceVersionNumber = () =>
  useScalingTemplateSelector((state) => state.sourceVersionNumber);
export const useScalingTemplateEditBaseFeatureCanonicalIds = () =>
  useScalingTemplateSelector((state) => state.editBaseFeatureCanonicalIds);
export const useScalingTemplateInstanceCount = () => useScalingTemplateSelector((state) => state.instanceCount);
export const useScalingTemplateInstanceNameFeature = () =>
  useScalingTemplateSelector((state) => state.instanceNameFeature);

// Array selectors
export const useScalingTemplateInputFeatures = () => useScalingTemplateSelector((s) => s.inputFeatures);
export const useScalingTemplateDerivedFeatures = () => useScalingTemplateSelector((s) => s.derivedFeatures);
export const useScalingTemplateExistingDerivedFeatures = () =>
  useScalingTemplateSelector((s) => s.existingDerivedFeatures);
export const useScalingTemplateExistingInputFeatures = () => useScalingTemplateSelector((s) => s.existingInputFeatures);
export const useScalingTemplateNewDerivedFeatures = () => useScalingTemplateSelector((s) => s.newDerivedFeatures);
export const useScalingTemplateNewInputFeatures = () => useScalingTemplateSelector((s) => s.newInputFeatures);

export const useScalingTemplateActions = () => useScalingTemplateStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestScalingTemplateStore(overrides?: Partial<ScalingTemplateState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestScalingTemplateStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialScalingTemplateState, ...overrides }, scalingTemplateActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateScalingTemplateStore() {
  return createStoreInstance<ScalingTemplateState, ScalingTemplateActions>(
    initialScalingTemplateState,
    scalingTemplateActionsFactory,
  );
}

export const usePersistScalingTemplateStore = () => {
  const register = useRegisterPersistedStore();
  const store = useScalingTemplateStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqScalingTemplateStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateScalingTemplateState,
    });
  }, [register, store]);
};
